import React, { useState } from 'react';
import CustomAnimationDialog from './CustomAnimationDialog';
import {
  Typography,
  Box,
  Slider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';

const animations = [
  {
    name: 'Classic Rainbow',
    code: 2,
    description: 'Smooth rainbow cycling',
    category: 'rainbow',
  },
  {
    name: 'USA',
    code: 3,
    description: 'Red, white, and blue patriotic pattern',
    category: 'rainbow',
  },
  {
    name: 'Gradual Rainbow',
    code: 10,
    description: 'Slow rainbow transition',
    category: 'rainbow',
  },
  {
    name: 'Running Rainbow',
    code: 12,
    description: 'Rainbow shifts across LEDs',
    category: 'rainbow',
  },
  {
    name: 'Juggle',
    code: 14,
    description: 'Bouncing dots of color',
    category: 'basic',
  },
  {
    name: 'Party',
    code: 15,
    description: 'Rapid, festive color flashes',
    category: 'basic',
  },
  {
    name: 'Police',
    code: 16,
    description: 'Flashing red and blue',
    category: 'basic',
  },
  {
    name: 'Random Fade',
    code: 17,
    description: 'Colors fading randomly',
    category: 'basic',
  },
  {
    name: 'Random Flash',
    code: 18,
    description: 'Colors flashing randomly',
    category: 'basic',
  },
  {
    name: 'Wave',
    code: 19,
    description: 'Moving wave effect',
    category: 'basic',
  },
  {
    name: 'Snake',
    code: 20,
    description: 'Snake-like movement',
    category: 'basic',
  },
  {
    name: 'Snake Fill',
    code: 21,
    description: 'Snake that fills the strip',
    category: 'basic',
  },
  {
    name: 'Erase',
    code: 22,
    description: 'Erase LEDs gradually',
    category: 'basic',
  },
  {
    name: 'Sparkle',
    code: 23,
    description: 'Random sparkling lights',
    category: 'basic',
  },
];

const buildAnimationMessage = (channel, width, height, animation, speed) => {
  return (
    `${channel}` +
    `${width.toString().padStart(3, '0')}` +
    `${height.toString().padStart(3, '0')}` +
    `${animation.code.toString().padStart(2, '0')}` +
    `${speed.toString().padStart(1, '0')}`
  );
};

const Animations = ({ handleSendAnimationToPhone }) => {
  const [animationSpeed, setAnimationSpeed] = useState(5);
  const [animationType, setAnimationType] = useState('rainbow');
  const [selectedAnimation, setSelectedAnimation] = useState(null);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [editAnimIndex, setEditAnimIndex] = useState(null);

  const [customAnimations, setCustomAnimations] = useState(() => {
    return JSON.parse(localStorage.getItem('customAnimations')) || [];
  });

  const saveCustomAnimations = (list) => {
    setCustomAnimations(list);
    localStorage.setItem('customAnimations', JSON.stringify(list));
  };

  const handleSpeedChange = (event, newValue) => {
    setAnimationSpeed(newValue);
  };

  const handleAnimationTypeChange = (event) => {
    setAnimationType(event.target.value);
  };

  const handleAnimationSelect = (animation) => {
    setSelectedAnimation(animation);

    if (animation.code) {
      const message = buildAnimationMessage(
        0,
        6,
        50,
        animation,
        animationSpeed
      );
      console.log('Send animation message:', message);
      handleSendAnimationToPhone?.(animation.code, animationSpeed);
    } else {
      console.log('Custom animation selected:', animation);
    }
  };

  const rainbowAnimations = animations.filter((a) => a.category === 'rainbow');
  const basicAnimations = animations.filter((a) => a.category === 'basic');

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Animations Page
      </Typography>

      {/* Animation Type + Speed */}
      <Paper sx={{ padding: 3, marginTop: 3, borderRadius: 2, boxShadow: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 2 }}>
          Animation Settings
        </Typography>
        <Box sx={{ marginBottom: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="animation-type-label">Animation Type</InputLabel>
            <Select
              labelId="animation-type-label"
              value={animationType}
              label="Animation Type"
              onChange={handleAnimationTypeChange}
            >
              <MenuItem value="rainbow">Rainbow</MenuItem>
              <MenuItem value="basic">Basic Animations</MenuItem>
              <MenuItem value="custom">Custom Animations</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography gutterBottom>
            Animation Speed: {animationSpeed}
          </Typography>
          <Slider
            value={animationSpeed}
            onChange={handleSpeedChange}
            valueLabelDisplay="auto"
            step={1}
            min={1}
            max={9}
          />
        </Box>
      </Paper>

      {/* Selected Animation */}
      {selectedAnimation && (
        <Paper
          sx={{
            padding: 2,
            marginTop: 3,
            borderRadius: 2,
            boxShadow: 1,
            backgroundColor: '#e3f2fd',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Selected Animation: {selectedAnimation.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {selectedAnimation.description}
          </Typography>
        </Paper>
      )}

      {/* Rainbow Animations */}
      {animationType === 'rainbow' && (
        <Box sx={{ marginTop: 3 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {rainbowAnimations.map((animation) => (
                  <TableRow
                    key={animation.name}
                    onClick={() => handleAnimationSelect(animation)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f0f0f0' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1">{animation.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {animation.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Basic Animations */}
      {animationType === 'basic' && (
        <Box sx={{ marginTop: 3 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {basicAnimations.map((animation) => (
                  <TableRow
                    key={animation.name}
                    onClick={() => handleAnimationSelect(animation)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f0f0f0' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1">{animation.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {animation.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Custom Animations */}
      {animationType === 'custom' && (
        <Box sx={{ marginTop: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setEditAnimIndex(null);
              setShowCustomDialog(true);
            }}
          >
            Create New Animation
          </Button>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableBody>
                {customAnimations.map((animation, idx) => (
                  <TableRow
                    key={idx}
                    onClick={() => handleAnimationSelect(animation)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f0f0f0' },
                    }}
                  >
                    <TableCell sx={{ width: '100%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          width: '100%',
                          gap: 0,
                        }}
                      >
                        {/* Left: name + description */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1">
                            {animation.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            noWrap={false}
                          >
                            {animation.description}
                          </Typography>
                        </Box>

                        {/* Right: color swatches, tight grid */}
                        {animation.colors && (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 12px)',
                              gap: '2px',
                              marginRight: 0,
                            }}
                          >
                            {animation.colors.map((c, i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: c,
                                  border: '1px solid #aaa',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditAnimIndex(idx);
                          setShowCustomDialog(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          const updated = customAnimations.filter(
                            (_, i) => i !== idx
                          );
                          saveCustomAnimations(updated);
                        }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomAnimationDialog
            open={showCustomDialog}
            onClose={() => setShowCustomDialog(false)}
            initialData={
              editAnimIndex != null ? customAnimations[editAnimIndex] : null
            }
            onSave={(anim) => {
              let updated;
              if (editAnimIndex != null) {
                updated = [...customAnimations];
                updated[editAnimIndex] = anim; // update existing
              } else {
                updated = [...customAnimations, anim]; // add new
              }
              saveCustomAnimations(updated);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Animations;
